/**
 * Hook for managing sub-workflow execution in master workflows
 *
 * Provides functionality for:
 * - Starting child workflows from SubWorkflow nodes
 * - Monitoring child workflow progress
 * - Aggregating progress across all child workflows
 * - Handling workflow hierarchies (parent/child instances)
 *
 * @module useSubWorkflowExecution
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from './useAuth';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuditLogger } from './useAuditLogger';

// ============================================================================
// GraphQL Operations
// ============================================================================

const GET_CHILD_WORKFLOWS = gql`
  query GetChildWorkflows($parentInstanceId: ID!, $tenantId: ID!) {
    childWorkflows(parentInstanceId: $parentInstanceId, tenantId: $tenantId) {
      edges {
        node {
          id
          workflowName
          workflowVersion
          status
          progressPercent
          startedAt
          completedAt
          parentInstanceId
          rootInstanceId
          executionMode
          totalSteps
          completedSteps
          failedSteps
          activeSteps
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const GET_AGGREGATED_PROGRESS = gql`
  query GetAggregatedProgress($parentInstanceId: ID!, $tenantId: ID!) {
    aggregatedProgress(parentInstanceId: $parentInstanceId, tenantId: $tenantId) {
      parentInstanceId
      totalChildren
      completedChildren
      failedChildren
      runningChildren
      overallProgressPercent
      children {
        childInstanceId
        workflowName
        status
        progressPercent
        startedAt
        completedAt
        lastActivity
        currentStep
        totalSteps
      }
    }
  }
`;

const GET_INSTANCE_HIERARCHY = gql`
  query GetInstanceHierarchy($instanceId: ID!, $tenantId: ID!, $depth: Int) {
    instanceHierarchy(instanceId: $instanceId, tenantId: $tenantId, depth: $depth) {
      instanceId
      parentInstanceId
      rootInstanceId
      workflowName
      workflowVersion
      status
      progressPercent
      depth
      children {
        instanceId
        parentInstanceId
        rootInstanceId
        workflowName
        workflowVersion
        status
        progressPercent
        depth
      }
    }
  }
`;

const START_SUB_WORKFLOW = gql`
  mutation StartSubWorkflow($input: StartSubWorkflowInput!) {
    startSubWorkflow(input: $input) {
      success
      childInstanceId
      childExecutionId
      errorMessage
    }
  }
`;

const CANCEL_SUB_WORKFLOW = gql`
  mutation CancelSubWorkflow(
    $childInstanceId: ID!
    $parentInstanceId: ID!
    $tenantId: ID!
    $reason: String
  ) {
    cancelSubWorkflow(
      childInstanceId: $childInstanceId
      parentInstanceId: $parentInstanceId
      tenantId: $tenantId
      reason: $reason
    ) {
      success
      errorMessage
    }
  }
`;

const SUB_WORKFLOW_PROGRESS = gql`
  subscription SubWorkflowProgress($parentInstanceId: ID!, $tenantId: ID!) {
    subWorkflowProgress(parentInstanceId: $parentInstanceId, tenantId: $tenantId) {
      parentInstanceId
      childInstanceId
      workflowName
      status
      progressPercent
      currentStep
      totalSteps
      timestamp
    }
  }
`;

const INSTANCE_HIERARCHY_UPDATED = gql`
  subscription InstanceHierarchyUpdated($rootInstanceId: ID!, $tenantId: ID!) {
    instanceHierarchyUpdated(rootInstanceId: $rootInstanceId, tenantId: $tenantId) {
      eventType
      instanceId
      parentInstanceId
      status
      timestamp
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type SubWorkflowExecutionMode = 'Inline' | 'Async' | 'Parallel';

export type SubWorkflowFailureMode =
  | 'FailParent'
  | 'SkipAndContinue'
  | 'RetryChild'
  | 'Compensate';

export type SubWorkflowStatus =
  | 'Pending'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Cancelled';

export interface ChildWorkflow {
  id: string;
  workflowName: string;
  workflowVersion: string;
  status: SubWorkflowStatus;
  progressPercent: number;
  startedAt: string | null;
  completedAt: string | null;
  parentInstanceId: string;
  rootInstanceId: string;
  executionMode: SubWorkflowExecutionMode;
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  activeSteps: number;
}

export interface ChildWorkflowProgress {
  childInstanceId: string;
  workflowName: string;
  status: SubWorkflowStatus;
  progressPercent: number;
  startedAt: string | null;
  completedAt: string | null;
  lastActivity: string | null;
  currentStep: string | null;
  totalSteps: number;
}

export interface AggregatedProgress {
  parentInstanceId: string;
  totalChildren: number;
  completedChildren: number;
  failedChildren: number;
  runningChildren: number;
  overallProgressPercent: number;
  children: ChildWorkflowProgress[];
}

export interface InstanceHierarchyNode {
  instanceId: string;
  parentInstanceId: string | null;
  rootInstanceId: string;
  workflowName: string;
  workflowVersion: string;
  status: SubWorkflowStatus;
  progressPercent: number;
  depth: number;
  children?: InstanceHierarchyNode[];
}

export interface StartSubWorkflowInput {
  parentNodeId: string;
  parentInstanceId: string;
  tenantId: string;
  workflowName: string;
  workflowVersion?: string;
  executionMode: SubWorkflowExecutionMode;
  failureMode: SubWorkflowFailureMode;
  inputData?: Record<string, unknown>;
  inheritContext?: boolean;
  timeoutMs?: number;
}

export interface StartSubWorkflowResult {
  success: boolean;
  childInstanceId?: string;
  childExecutionId?: string;
  errorMessage?: string;
}

export interface SubWorkflowProgressEvent {
  parentInstanceId: string;
  childInstanceId: string;
  workflowName: string;
  status: SubWorkflowStatus;
  progressPercent: number;
  currentStep: string | null;
  totalSteps: number;
  timestamp: string;
}

export interface UseSubWorkflowExecutionOptions {
  /** Parent instance ID to monitor */
  parentInstanceId: string | null;
  /** Root instance ID for hierarchy tracking (defaults to parentInstanceId) */
  rootInstanceId?: string;
  /** Enable real-time progress subscription */
  enableSubscription?: boolean;
  /** Maximum depth for hierarchy queries */
  hierarchyDepth?: number;
  /** Polling interval in ms (0 to disable) */
  pollingInterval?: number;
}

export interface UseSubWorkflowExecutionResult {
  // Data
  childWorkflows: ChildWorkflow[];
  aggregatedProgress: AggregatedProgress | null;
  hierarchy: InstanceHierarchyNode | null;

  // Loading states
  loading: boolean;
  progressLoading: boolean;
  hierarchyLoading: boolean;

  // Errors
  error: Error | null;
  progressError: Error | null;
  hierarchyError: Error | null;

  // Mutations
  startSubWorkflow: (
    input: Omit<StartSubWorkflowInput, 'tenantId' | 'parentInstanceId'>
  ) => Promise<StartSubWorkflowResult>;
  cancelSubWorkflow: (
    childInstanceId: string,
    reason?: string
  ) => Promise<{ success: boolean; errorMessage?: string }>;

  // Refresh functions
  refetchChildren: () => Promise<void>;
  refetchProgress: () => Promise<void>;
  refetchHierarchy: () => Promise<void>;

  // Real-time events
  latestProgressEvent: SubWorkflowProgressEvent | null;

  // Computed values
  isAnyChildRunning: boolean;
  isAnyChildFailed: boolean;
  allChildrenComplete: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export const useSubWorkflowExecution = ({
  parentInstanceId,
  rootInstanceId,
  enableSubscription = true,
  hierarchyDepth = 5,
  pollingInterval = 0,
}: UseSubWorkflowExecutionOptions): UseSubWorkflowExecutionResult => {
  const { currentUser } = useAuth();
  const { tenantId } = useCurrentTenant();
  const { logUserAction } = useAuditLogger();

  const [latestProgressEvent, setLatestProgressEvent] =
    useState<SubWorkflowProgressEvent | null>(null);

  const effectiveRootId = rootInstanceId || parentInstanceId;

  // Query child workflows
  const {
    data: childWorkflowsData,
    loading: childWorkflowsLoading,
    error: childWorkflowsError,
    refetch: refetchChildWorkflows,
  } = useQuery<Record<string, any>>(GET_CHILD_WORKFLOWS, {
    variables: { parentInstanceId, tenantId },
    skip: !parentInstanceId || !tenantId,
    pollInterval: pollingInterval,
  });

  // Query aggregated progress
  const {
    data: progressData,
    loading: progressLoading,
    error: progressError,
    refetch: refetchProgress,
  } = useQuery<Record<string, any>>(GET_AGGREGATED_PROGRESS, {
    variables: { parentInstanceId, tenantId },
    skip: !parentInstanceId || !tenantId,
    pollInterval: pollingInterval,
  });

  // Query instance hierarchy
  const {
    data: hierarchyData,
    loading: hierarchyLoading,
    error: hierarchyError,
    refetch: refetchHierarchy,
  } = useQuery<Record<string, any>>(GET_INSTANCE_HIERARCHY, {
    variables: { instanceId: effectiveRootId, tenantId, depth: hierarchyDepth },
    skip: !effectiveRootId || !tenantId,
  });

  // Subscribe to real-time progress updates
  useSubscription<Record<string, any>>(SUB_WORKFLOW_PROGRESS, {
    variables: { parentInstanceId, tenantId },
    skip: !parentInstanceId || !tenantId || !enableSubscription,
    onData: ({ data }) => {
      if (data?.data?.subWorkflowProgress) {
        setLatestProgressEvent(data.data.subWorkflowProgress);
        // Also refetch to update queries
        refetchProgress();
      }
    },
  });

  // Subscribe to hierarchy updates
  useSubscription<Record<string, any>>(INSTANCE_HIERARCHY_UPDATED, {
    variables: { rootInstanceId: effectiveRootId, tenantId },
    skip: !effectiveRootId || !tenantId || !enableSubscription,
    onData: () => {
      // Refetch hierarchy on updates
      refetchHierarchy();
    },
  });

  // Start sub-workflow mutation
  const [startSubWorkflowMutation] = useMutation<Record<string, any>>(START_SUB_WORKFLOW);

  const startSubWorkflow = useCallback(
    async (
      input: Omit<StartSubWorkflowInput, 'tenantId' | 'parentInstanceId'>
    ): Promise<StartSubWorkflowResult> => {
      if (!parentInstanceId || !tenantId) {
        return {
          success: false,
          errorMessage: 'Missing parent instance or tenant ID',
        };
      }

      try {
        const { data } = await startSubWorkflowMutation({
          variables: {
            input: {
              ...input,
              parentInstanceId,
              tenantId,
            },
          },
        });

        const result = data?.startSubWorkflow;

        if (result?.success) {
          logUserAction({
            action: 'START_SUB_WORKFLOW',
            details: {
              entityType: 'WorkflowInstance',
              entityId: result.childInstanceId || '',
              parentInstanceId,
              workflowName: input.workflowName,
              executionMode: input.executionMode,
            },
          });

          // Refresh data
          await Promise.all([refetchChildWorkflows(), refetchProgress()]);
        }

        return result || { success: false, errorMessage: 'No response from server' };
      } catch (error) {
        return {
          success: false,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [
      parentInstanceId,
      tenantId,
      startSubWorkflowMutation,
      logUserAction,
      refetchChildWorkflows,
      refetchProgress,
    ]
  );

  // Cancel sub-workflow mutation
  const [cancelSubWorkflowMutation] = useMutation<Record<string, any>>(CANCEL_SUB_WORKFLOW);

  const cancelSubWorkflow = useCallback(
    async (
      childInstanceId: string,
      reason?: string
    ): Promise<{ success: boolean; errorMessage?: string }> => {
      if (!parentInstanceId || !tenantId) {
        return { success: false, errorMessage: 'Missing parent instance or tenant ID' };
      }

      try {
        const { data } = await cancelSubWorkflowMutation({
          variables: {
            childInstanceId,
            parentInstanceId,
            tenantId,
            reason,
          },
        });

        const result = data?.cancelSubWorkflow;

        if (result?.success) {
          logUserAction({
            action: 'CANCEL_SUB_WORKFLOW',
            details: {
              entityType: 'WorkflowInstance',
              entityId: childInstanceId,
              parentInstanceId,
              reason,
            },
          });

          // Refresh data
          await Promise.all([refetchChildWorkflows(), refetchProgress()]);
        }

        return result || { success: false, errorMessage: 'No response from server' };
      } catch (error) {
        return {
          success: false,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    },
    [
      parentInstanceId,
      tenantId,
      cancelSubWorkflowMutation,
      logUserAction,
      refetchChildWorkflows,
      refetchProgress,
    ]
  );

  // Extract data from query results
  const childWorkflows: ChildWorkflow[] = useMemo(() => {
    if (!childWorkflowsData?.childWorkflows?.edges) return [];
    return childWorkflowsData.childWorkflows.edges.map(
      (edge: { node: ChildWorkflow }) => edge.node
    );
  }, [childWorkflowsData]);

  const aggregatedProgress: AggregatedProgress | null = useMemo(() => {
    return progressData?.aggregatedProgress ?? null;
  }, [progressData]);

  const hierarchy: InstanceHierarchyNode | null = useMemo(() => {
    return hierarchyData?.instanceHierarchy ?? null;
  }, [hierarchyData]);

  // Computed values
  const isAnyChildRunning = useMemo(() => {
    return childWorkflows.some((c) => c.status === 'Running');
  }, [childWorkflows]);

  const isAnyChildFailed = useMemo(() => {
    return childWorkflows.some((c) => c.status === 'Failed');
  }, [childWorkflows]);

  const allChildrenComplete = useMemo(() => {
    if (childWorkflows.length === 0) return false;
    return childWorkflows.every(
      (c) => c.status === 'Completed' || c.status === 'Cancelled'
    );
  }, [childWorkflows]);

  // Combine loading states
  const loading = childWorkflowsLoading;

  // Combine errors (return first error)
  const error = childWorkflowsError ?? null;

  return {
    // Data
    childWorkflows,
    aggregatedProgress,
    hierarchy,

    // Loading states
    loading,
    progressLoading,
    hierarchyLoading,

    // Errors
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    progressError: progressError instanceof Error ? progressError : null,
    hierarchyError: hierarchyError instanceof Error ? hierarchyError : null,

    // Mutations
    startSubWorkflow,
    cancelSubWorkflow,

    // Refresh functions
    refetchChildren: async () => {
      await refetchChildWorkflows();
    },
    refetchProgress: async () => {
      await refetchProgress();
    },
    refetchHierarchy: async () => {
      await refetchHierarchy();
    },

    // Real-time events
    latestProgressEvent,

    // Computed values
    isAnyChildRunning,
    isAnyChildFailed,
    allChildrenComplete,
  };
};

export default useSubWorkflowExecution;
